import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { debounce } from "lodash";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

export interface ComboboxItem {
    id: string;
    [key: string]: any;
}

export interface PaginationResponse<T> {
    data: T[];
    pagination: {
        has_next: boolean;
    };
}

export interface QueryFnParams {
    pageParam: number;
    search: string;
    [key: string]: any;
}

export interface InfiniteComboboxProps<T extends ComboboxItem> {
    // Query configuration
    queryKey: string | string[];
    queryFn: (params: QueryFnParams) => Promise<PaginationResponse<T>>;
    additionalFilters?: Record<string, any>;

    // Display configuration
    getDisplayValue: (item: T) => string;
    renderItem?: (item: T) => React.ReactNode;

    // Selection handling
    onSelect: (item: T | null) => void;
    initialValue?: string;
    initialItem?: T;
    defaultValue?: T | null; // New prop for full object support

    // UI customization
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    width?: string;
    disabled?: boolean;

    // Query options
    pageSize?: number;
    debounceMs?: number;
}

export default function ComboBox<T extends ComboboxItem>({
    queryKey,
    queryFn,
    additionalFilters = {},
    getDisplayValue,
    renderItem,
    onSelect,
    initialValue,
    initialItem,
    defaultValue,
    placeholder = "Select item...",
    searchPlaceholder = "Search...",
    className = "",
    width = "w-[400px]",
    disabled = false,
    pageSize = 10,
    debounceMs = 300,
}: InfiniteComboboxProps<T>) {
    const { ref, inView } = useInView();

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(initialValue);
    const [search, setSearch] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [internalSelectedItem, setInternalSelectedItem] = useState<T | null>(
        null,
    );

    // Determine which item to display
    const effectiveSelectedItem =
        internalSelectedItem ?? defaultValue ?? initialItem;
    const effectiveValue =
        internalSelectedItem?.id ?? defaultValue?.id ?? value;

    useEffect(() => {
        if (!internalSelectedItem) {
            setValue(initialValue);
        }
    }, [initialValue, internalSelectedItem]);

    const fullQueryKey = Array.isArray(queryKey)
        ? [...queryKey, search, ...Object.values(additionalFilters)]
        : [queryKey, search, ...Object.values(additionalFilters)];

    const { fetchNextPage, hasNextPage, ...query } = useInfiniteQuery({
        queryKey: [fullQueryKey],
        queryFn: async ({ pageParam }) =>
            queryFn({
                pageParam,
                search,
                limit: pageSize,
                filter: additionalFilters,
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPage) =>
            lastPage?.pagination?.has_next
                ? allPage.length * pageSize
                : undefined,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (inView && hasNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, inView, hasNextPage]);

    // Debounced search handler
    const handleInputChange = useCallback(
        debounce((value: string) => {
            setSearch(value);
        }, debounceMs),
        [debounceMs],
    );

    // Update input value immediately for responsive feel
    const handleInput: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value;
        setInputValue(value);
        handleInputChange(value);
    };

    // Clear input when popover closes
    useEffect(() => {
        if (!open) {
            setInputValue("");
            setSearch("");
        }
    }, [open]);

    const handleSelect = (currentValue: string) => {
        setValue(currentValue);
        setOpen(false);
        const allItems = [
            ...(query.data?.pages?.flatMap((page) => page?.data ?? []) ?? []),
            ...(effectiveSelectedItem ? [effectiveSelectedItem] : []),
        ];
        const newSelectedItem =
            allItems.find((item) => item?.id === currentValue) ?? null;

        // Store the newly selected item internally
        setInternalSelectedItem(newSelectedItem);
        onSelect(newSelectedItem);
    };

    // Get all items from query, excluding the effectiveSelectedItem to avoid duplicates
    const allItems = [
        ...(
            query.data?.pages?.flatMap((page) => page?.data ?? []) ?? []
        ).filter((item) => item && item.id !== effectiveSelectedItem?.id),
    ].filter(Boolean); // Remove any null/undefined items

    const displaySelectedItem =
        allItems.find((item) => item?.id === effectiveValue) ??
        effectiveSelectedItem;

    // Default render function if none provided
    const defaultRenderItem = (item: T) => (
        <div className="flex flex-col">
            <span>{getDisplayValue(item)}</span>
        </div>
    );

    const itemRenderer = renderItem || defaultRenderItem;

    // Get items count safely
    const itemsCount = allItems.length;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label="Select item"
                    className={cn(width, "justify-between", className)}
                    disabled={query.isError || disabled}
                >
                    <span className="truncate">
                        {displaySelectedItem
                            ? getDisplayValue(displaySelectedItem)
                            : placeholder}
                    </span>
                    {query.isFetching ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin opacity-50" />
                    ) : (
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn(width, "p-0")}>
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={inputValue}
                        onInput={handleInput}
                        className="h-9"
                    />
                    <CommandList>
                        {query.isError ? (
                            <CommandEmpty className="py-6 text-center text-sm">
                                Error loading items. Please try again.
                            </CommandEmpty>
                        ) : query.isLoading ? (
                            <CommandEmpty className="py-6 text-center">
                                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                            </CommandEmpty>
                        ) : itemsCount === 0 && !effectiveSelectedItem ? (
                            <CommandEmpty>No items found.</CommandEmpty>
                        ) : (
                            <CommandGroup>
                                {effectiveSelectedItem && (
                                    <CommandItem
                                        key={effectiveSelectedItem.id}
                                        value={effectiveSelectedItem.id}
                                        onSelect={handleSelect}
                                        className="flex items-center justify-between"
                                    >
                                        {itemRenderer(effectiveSelectedItem)}
                                        <Check
                                            className={cn(
                                                "ml-auto h-4 w-4 shrink-0",
                                                effectiveValue ===
                                                    effectiveSelectedItem.id
                                                    ? "opacity-100"
                                                    : "opacity-0",
                                            )}
                                        />
                                    </CommandItem>
                                )}
                                {allItems.map((item, index) => (
                                    <CommandItem
                                        key={`${index}-${item.id}`}
                                        value={item.id}
                                        onSelect={handleSelect}
                                        className="flex items-center justify-between"
                                    >
                                        {itemRenderer(item)}
                                        <Check
                                            className={cn(
                                                "ml-auto h-4 w-4 shrink-0",
                                                effectiveValue === item.id
                                                    ? "opacity-100"
                                                    : "opacity-0",
                                            )}
                                        />
                                    </CommandItem>
                                ))}
                                {(hasNextPage || query.isFetchingNextPage) && (
                                    <div className="flex justify-center py-2">
                                        <button
                                            ref={ref}
                                            disabled
                                            className="text-sm text-gray-500"
                                        >
                                            {query.isFetchingNextPage ||
                                            hasNextPage
                                                ? "Loading more..."
                                                : "Nothing more to load"}
                                        </button>
                                    </div>
                                )}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
